import Image from "next/image";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import Windows from "@/assets/img/icons/download.svg";
import BannerSlide, { BannerSlideProps } from "./BannerSlide";

const SLIDE_DURATION_MS = 8000;
// Outgoing slide fades out fully before the incoming one starts fading in,
// so titles never visually overlap mid-transition.
const FADE_DURATION = 0.4;

const Banner = () => {
  const t1 = useTranslations("home/banner");
  const t2 = useTranslations("home/banner-2027");
  const t3 = useTranslations("home/banner-slide-3");
  const t4 = useTranslations("home/banner-slide-4");
  const [activeIndex, setActiveIndex] = useState(0);

  const slides: Omit<BannerSlideProps, "active">[] = [
    {
      badge: (
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
          <span className="rounded-full bg-yellow px-2.5 py-1 text-black">
            {t2("badge-new")}
          </span>
          <span className="text-white/80">{t2("badge-label")}</span>
        </div>
      ),
      titlePart1: t2("title-1"),
      titlePart2: t2("title-2"),
      accentClassName: "text-[#B4AEEA]",
      description: t2("description"),
      primaryHref: "/download",
      primaryLabel: t2("download-button"),
      primaryIcon: (
        <Image
          alt="download"
          width={24}
          height={24}
          src={Windows}
          className="mr-2"
        />
      ),
      primaryClassName: "bg-yellow border-yellow text-black",
      secondaryHref: "/software/novidades",
      secondaryLabel: t2("secondary-button"),
      trustItems: [t2("trust-1"), t2("trust-2"), t2("trust-3")],
      background: {
        desktopMp4:
          "/assets/banner/slide-2/visualorc_2027-1_desktop_fundo_animado.mp4",
        desktopWebm:
          "/assets/banner/slide-2/visualorc_2027-1_desktop_fundo_animado.webm",
        desktopPoster:
          "/assets/banner/slide-2/visualorc_2027-1_desktop_fundo_1920x960@2x.png",
        mobileMp4:
          "/assets/banner/slide-2/visualorc_2027-1_mobile_fundo_animado.mp4",
        mobileWebm:
          "/assets/banner/slide-2/visualorc_2027-1_mobile_fundo_animado.webm",
        mobilePoster:
          "/assets/banner/slide-2/visualorc_2027-1_mobile_fundo_390x844@2x.png",
      },
    },
    {
      badge: (
        <div className="flex items-center gap-2 text-blue text-xs font-bold uppercase tracking-widest">
          <span className="h-px w-6 bg-blue" />
          {t1("kicker")}
        </div>
      ),
      titlePart1: t1("title-1"),
      titlePart2: t1("title-2"),
      accentClassName: "text-blue",
      description: t1("description"),
      primaryHref: "/download",
      primaryLabel: t1("download-button"),
      primaryIcon: (
        <Image
          alt="windows"
          width={30}
          height={30}
          src={Windows}
          className="mr-2"
        />
      ),
      primaryClassName: "",
      secondaryHref: "/planos-e-precos",
      secondaryLabel: t1("pricing-button"),
      trustItems: [t1("trust-1"), t1("trust-2"), t1("trust-3")],
      background: {
        desktopMp4: "/assets/banner/visualorc_hero_desktop_background.mp4",
        desktopWebm: "/assets/banner/visualorc_hero_desktop_background.webm",
        desktopPoster: "/assets/banner/visualorc_hero_desktop_background.gif",
        mobileMp4: "/assets/banner/visualorc_hero_mobile_background.mp4",
        mobileWebm: "/assets/banner/visualorc_hero_mobile_background.webm",
        mobilePoster: "/assets/banner/visualorc_hero_mobile_background.gif",
      },
    },
    {
      badge: (
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
          <span className="rounded-full bg-yellow px-2.5 py-1 text-black">
            {t3("badge-new")}
          </span>
          <span className="text-white/80">{t3("badge-label")}</span>
        </div>
      ),
      titlePart1: t3("title-1"),
      titlePart2: t3("title-2"),
      accentClassName: "text-[#6EE7B7]",
      description: t3("description"),
      primaryHref: "/download",
      primaryLabel: t3("download-button"),
      primaryIcon: (
        <Image
          alt="download"
          width={24}
          height={24}
          src={Windows}
          className="mr-2"
        />
      ),
      primaryClassName: "bg-yellow border-yellow text-black",
      secondaryHref: "/software/orcamentacao",
      secondaryLabel: t3("secondary-button"),
      trustItems: [t3("trust-1"), t3("trust-2"), t3("trust-3")],
      background: {
        desktopMp4:
          "/assets/banner/slide-3/visualorc_composicao-automatica_desktop_fundo_animado.mp4",
        desktopWebm:
          "/assets/banner/slide-3/visualorc_composicao-automatica_desktop_fundo_animado.webm",
        desktopPoster:
          "/assets/banner/slide-3/visualorc_composicao-automatica_desktop_fundo_animado.gif",
        mobileMp4:
          "/assets/banner/slide-3/visualorc_composicao-automatica_mobile_fundo_animado.mp4",
        mobileWebm:
          "/assets/banner/slide-3/visualorc_composicao-automatica_mobile_fundo_animado.webm",
        mobilePoster:
          "/assets/banner/slide-3/visualorc_composicao-automatica_mobile_fundo_animado.gif",
      },
    },
    {
      badge: (
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
          <span className="rounded-full bg-yellow px-2.5 py-1 text-black">
            {t4("badge-new")}
          </span>
          <span className="text-white/80">{t4("badge-label")}</span>
        </div>
      ),
      titlePart1: t4("title-1"),
      titlePart2: t4("title-2"),
      accentClassName: "text-[#F5A864]",
      description: t4("description"),
      primaryHref: "/download",
      primaryLabel: t4("download-button"),
      primaryIcon: (
        <Image
          alt="download"
          width={24}
          height={24}
          src={Windows}
          className="mr-2"
        />
      ),
      primaryClassName: "bg-yellow border-yellow text-black",
      secondaryHref: "/software/planeamento/elaborar-o-plano-de-trabalhos",
      secondaryLabel: t4("secondary-button"),
      trustItems: [t4("trust-1"), t4("trust-2"), t4("trust-3")],
      background: {
        desktopMp4:
          "/assets/banner/slide-4/visualorc_plano-automatico_desktop_fundo_animado.mp4",
        desktopWebm:
          "/assets/banner/slide-4/visualorc_plano-automatico_desktop_fundo_animado.webm",
        desktopPoster:
          "/assets/banner/slide-4/visualorc_plano-automatico_desktop_fundo_animado.gif",
        mobileMp4:
          "/assets/banner/slide-4/visualorc_plano-automatico_mobile_fundo_animado.mp4",
        mobileWebm:
          "/assets/banner/slide-4/visualorc_plano-automatico_mobile_fundo_animado.webm",
        mobilePoster:
          "/assets/banner/slide-4/visualorc_plano-automatico_mobile_fundo_animado.gif",
      },
    },
  ];

  const goToSlide = (index: number) =>
    setActiveIndex(((index % slides.length) + slides.length) % slides.length);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, SLIDE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [activeIndex, slides.length]);

  return (
    <div id="banner" className="relative min-h-[100svh]">
      {slides.map((slide, index) => {
        const isActive = index === activeIndex;
        return (
          <motion.div
            key={index}
            className="absolute inset-0"
            style={{ zIndex: isActive ? 1 : 0 }}
            initial={false}
            animate={{ opacity: isActive ? 1 : 0 }}
            transition={{
              duration: FADE_DURATION,
              delay: isActive ? FADE_DURATION : 0,
            }}
            aria-hidden={isActive ? undefined : true}
            inert={isActive ? undefined : true}
          >
            <BannerSlide {...slide} active={isActive} />
          </motion.div>
        );
      })}
      <button
        type="button"
        aria-label="Previous slide"
        onClick={() => goToSlide(activeIndex - 1)}
        className="hidden md:flex absolute left-4 top-1/2 z-10 h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 sm:left-6"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M15 6l-6 6 6 6" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Next slide"
        onClick={() => goToSlide(activeIndex + 1)}
        className="hidden md:flex absolute right-4 top-1/2 z-10 h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 sm:right-6"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>
      <div className="absolute inset-x-0 bottom-6 z-10 flex flex-col items-center gap-5">
        <div className="hidden md:flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Slide ${index + 1}`}
              onClick={() => goToSlide(index)}
              className={twMerge(
                "h-2 w-2 rounded-full bg-white/40 transition-all",
                index === activeIndex && "w-6 bg-white",
              )}
            />
          ))}
        </div>
        <motion.button
          type="button"
          aria-label="Scroll down"
          onClick={() =>
            document
              .getElementById("features")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="hidden md:block text-white/80 hover:text-white"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </motion.button>
      </div>
    </div>
  );
};

export default Banner;
